import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Box, Typography } from '@mui/material';
import { useIsNarrowScreen } from '../../common/useIsNarrowScreen';
import { FlashCard } from './FlashCard';
import { CardControls } from './CardControls';
import { CardHint } from './CardHint';
import { AudioButton } from '../../common/AudioButton';
import { FormToggle } from '../../common/Form/FormToggle';

const slideVariants = {
  enter: (direction) => ({ x: direction > 0 ? 260 : -260, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (direction) => ({ x: direction > 0 ? -260 : 260, opacity: 0 }),
};

const slideTransition = { duration: 0.28, ease: [0.4, 0, 0.2, 1] };

function StudyDeckBody({ deck, onCardChange }) {
  const isNarrow = useIsNarrowScreen();
  const cards = deck.cards ?? [];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [direction, setDirection] = useState(1);
  const [isShuffled, setIsShuffled] = useState(false);
  const [shuffledCards, setShuffledCards] = useState([]);

  useEffect(() => {
    setCurrentIndex(0);
    setIsFlipped(false);
    setDirection(1);
    setIsShuffled(false);
    setShuffledCards([]);
  }, [deck.id]);

  useEffect(() => {
    return () => window.speechSynthesis?.cancel();
  }, []);

  const activeCards = isShuffled ? shuffledCards : cards;
  const currentCard = activeCards[currentIndex];

  useEffect(() => {
    onCardChange?.(currentIndex + 1);
  }, [currentIndex, activeCards.length, onCardChange]);

  const navigate = (nextIndex, dir) => {
    window.speechSynthesis?.cancel();
    setDirection(dir);
    setCurrentIndex(nextIndex);
    setIsFlipped(false);
  };

  const handleNext = () => navigate(currentIndex + 1, 1);
  const handlePrev = () => navigate(currentIndex - 1, -1);
  const handleFlip = () => {
    window.speechSynthesis?.cancel();
    setIsFlipped((prev) => !prev);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' && currentIndex < activeCards.length - 1)
        navigate(currentIndex + 1, 1);
      if (e.key === 'ArrowLeft' && currentIndex > 0) navigate(currentIndex - 1, -1);
      if (e.key === ' ') {
        e.preventDefault();
        handleFlip();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, activeCards.length]);

  const handleShuffle = () => {
    window.speechSynthesis?.cancel();
    if (!isShuffled) {
      const shuffled = [...cards].sort(() => Math.random() - 0.5);
      setShuffledCards(shuffled);
    }
    setCurrentIndex(0);
    setIsFlipped(false);
    setIsShuffled((prev) => !prev);
  };

  if (cards.length === 0) {
    return (
      <Box id="studyDeck__box_content" className="studyDeckContent">
        <Typography id="studyDeck__text_noCards" variant="body2" color="text.secondary">
          No cards in this deck yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box id="studyDeck__box_content" className="studyDeckContent">
      <Box className="studyDeckContentToolbar">
        <Box className="toolbarLeft">
          <CardHint hint={currentCard.hint} />
          <AudioButton
            id="studyDeck__iconButton_audio"
            text={isFlipped ? currentCard.definition : currentCard.name}
            label="Read card aloud"
          />
        </Box>

        <FormToggle
          id="studyDeck__shuffle"
          label="Shuffle"
          checked={isShuffled}
          onChange={handleShuffle}
          className="shuffleToggle"
        />
      </Box>

      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={`${isShuffled}-${currentIndex}`}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={slideTransition}
          className="cardSlot"
        >
          <FlashCard card={currentCard} isFlipped={isFlipped} onFlip={handleFlip} />
        </motion.div>
      </AnimatePresence>

      <CardControls
        currentIndex={currentIndex}
        total={activeCards.length}
        onPrev={handlePrev}
        onNext={handleNext}
      />

      {isNarrow && <Typography className="tapToFlipHint">Tap card to flip</Typography>}
    </Box>
  );
}

export default StudyDeckBody;
